// User Detail Panel Component
// Side panel showing user information and team assignments

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Users as UsersIcon, Shield, Code, Settings, Package, ShieldCheck, Wrench } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import type { User, Team } from '@/types/teams';
import { ManageTeamsModal } from './ManageTeamsModal';

const TEAM_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  users: UsersIcon,
  shield: Shield,
  code: Code,
  settings: Settings,
  package: Package,
  'shield-check': ShieldCheck,
  wrench: Wrench,
};

interface UserDetailPanelProps {
  user: User;
  teams: Team[];
  onClose: () => void;
  onUpdate: () => void;
}

export function UserDetailPanel({
  user,
  teams,
  onClose,
  onUpdate,
}: Readonly<UserDetailPanelProps>) {
  const [showManageTeamsModal, setShowManageTeamsModal] = useState(false);

  const userTeams = user.teams
    .map(teamId => teams.find(t => t.id === teamId))
    .filter(Boolean) as Team[];

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <Sheet open={true} onOpenChange={onClose}>
        <SheetContent className="w-[480px] sm:max-w-[480px] p-0 flex flex-col">
          <SheetHeader className="p-6 pb-4 pr-14 border-b">
            <SheetTitle>User Details</SheetTitle>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="p-6 space-y-6">
              {/* User Header */}
              <div className="flex flex-col items-center text-center">
                <div className="h-20 w-20 rounded-md bg-primary/10 flex items-center justify-center mb-3">
                  <span className="text-2xl font-semibold text-primary">
                    {getInitials(user.name)}
                  </span>
                </div>
                <h2 className="text-xl font-semibold">{user.name}</h2>
                <p className="text-sm text-muted-foreground">{user.email}</p>
                <Badge
                  variant={user.status === 'active' ? 'default' : 'secondary'}
                  className="mt-2"
                >
                  {user.status === 'active' ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              {/* Divider */}
              <div className="border-t" />

              {/* Information Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">Information</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-muted-foreground">Last Login</span>
                    <span className="text-sm text-right">
                      {user.lastLogin
                        ? formatDistanceToNow(new Date(user.lastLogin), { addSuffix: true })
                        : 'Never'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-muted-foreground">Member Since</span>
                    <span className="text-sm text-right">
                      {formatDistanceToNow(new Date(user.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-muted-foreground">User ID</span>
                    <span className="text-sm text-right font-mono">{user.id}</span>
                  </div>
                  
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-muted-foreground">Source</span>
                    <span className="text-sm text-right">{user.source}</span>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t" />

              {/* Team Assignments Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm">
                    Team Memberships
                    <Badge variant="secondary" className="ml-2">
                      {userTeams.length}
                    </Badge>
                  </h3>
                  <Button
                    size="sm"
                    onClick={() => setShowManageTeamsModal(true)}
                  >
                    Manage Teams
                  </Button>
                </div>

                {userTeams.length === 0 ? (
                  <div className="text-center py-6 text-sm text-muted-foreground">
                    <p>No team assignments</p>
                    <p className="mt-1">This user must be assigned to at least one team</p>
                    <Button
                      size="sm"
                      className="mt-3"
                      onClick={() => setShowManageTeamsModal(true)}
                    >
                      Assign to Standard Users Team
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {userTeams.map(team => {
                      const IconComponent = TEAM_ICONS[team.icon] || UsersIcon;
                      return (
                        <div
                          key={team.id}
                          className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                        >
                          <div
                            className="h-4 w-1 rounded-full"
                            style={{ backgroundColor: team.color }}
                          />
                          <div
                            className="h-8 w-8 rounded-md flex items-center justify-center"
                            style={{ backgroundColor: `${team.color}20`, color: team.color }}
                          >
                            <IconComponent className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">{team.name}</p>
                              {team.isDefault && (
                                <Badge variant="outline" className="text-xs">
                                  Default
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="p-6 border-t">
            <Button
              variant="outline"
              className="w-full"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Manage Teams Modal */}
      {showManageTeamsModal && (
        <ManageTeamsModal
          users={[user]}
          teams={teams}
          onClose={() => setShowManageTeamsModal(false)}
          onSave={() => {
            setShowManageTeamsModal(false);
            onUpdate();
          }}
        />
      )}
    </>
  );
}
